import React, { useMemo } from 'react'
import { useStyles } from './CustomFormModal.styles'
import clsx from 'clsx'
import { Grid, Typography } from '@mui/material'
import {
  ModalConfirmV2,
  ModalSizeVariants,
  SelectV2,
  TextInputV2,
} from '@labrav/react-components'
import { FieldArray, useFormikContext } from 'formik'
import { faShelves, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import awsLogo from '../../../../../common/images/awsCloud.png'
import { getRandom } from '../../../../../common/utils/helperFunctions'
import { get, isEmpty } from 'lodash'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import {
  dataType,
  requiredField,
  validationType,
} from '../../../../../common/utils/OnboardingDataSelect'
import { formatToApiName } from '../../../../../common/utils/productCreationUtil'
import { ListingCardInCreation } from '../../../../../modules/storefront/listingCardCreation/reducer'

export interface CustomFormModalProps {
  keyName: string
  open: boolean
  handleClose: () => void
  title: string
}

export const CustomFormModal: React.FC<CustomFormModalProps> = ({
  keyName,
  open,
  handleClose,
  title,
}) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, setFieldValue, handleChange } =
    useFormikContext<ListingCardInCreation>()
  const { listingCardData } = values

  const customFormFields =
    keyName === 'privateOffer'
      ? listingCardData?.template?.data?.privateOfferForm?.fieldsToDisplay || []
      : listingCardData?.template?.data?.requestDemoForm?.fieldsToDisplay || []

  const randomKey = useMemo(() => getRandom(), [customFormFields.length])
  const customFormFieldsTouched =
    keyName === 'privateOffer'
      ? touched?.listingCardData?.template?.data?.privateOfferForm
      : touched?.listingCardData?.template?.data?.requestDemoForm

  const customFormFieldsErrors =
    keyName === 'privateOffer'
      ? errors?.listingCardData?.template?.data?.privateOfferForm || []
      : errors?.listingCardData?.template?.data?.requestDemoForm || []

  const acceptButtonDisabled = useMemo(() => {
    const currentFields =
      keyName === 'privateOffer'
        ? listingCardData?.template?.data?.privateOfferForm?.fieldsToDisplay ||
          []
        : listingCardData?.template?.data?.requestDemoForm?.fieldsToDisplay ||
          []

    const hasEmptyRequiredFields = currentFields.some(
      field => field.isRequired && !field.displayName.trim()
    )

    const hasInvalidLength = currentFields.some(
      field => field.minLength > field.maxLength
    )

    const hasErrors = currentFields.some((_, index) => {
      const displayNameError = get(
        customFormFieldsErrors,
        `fieldsToDisplay.${index}.displayName`
      )
      return !!displayNameError
    })

    return hasEmptyRequiredFields || hasErrors || hasInvalidLength
  }, [listingCardData?.template?.data, customFormFieldsErrors])

  const handleDataTypeChange = (
    keyName: string,
    index: number,
    value: string
  ) => {
    const formKey =
      keyName === 'privateOffer' ? 'privateOfferForm' : 'requestDemoForm'
    setFieldValue(
      `listingCardData.template.data.${formKey}.fieldsToDisplay[${index}].dataType`,
      value
    )
  }

  const handleIsRequiredChange = (
    keyName: string,
    index: number,
    value: string
  ) => {
    const formKey =
      keyName === 'privateOffer' ? 'privateOfferForm' : 'requestDemoForm'
    setFieldValue(
      `listingCardData.template.data.${formKey}.fieldsToDisplay[${index}].isRequired`,
      value === 'Yes' ? true : false
    )
  }

  const handleValidationTypeChange = (
    keyName: string,
    index: number,
    value: string
  ) => {
    const formKey =
      keyName === 'privateOffer' ? 'privateOfferForm' : 'requestDemoForm'
    setFieldValue(
      `listingCardData.template.data.${formKey}.fieldsToDisplay[${index}].validationType`,
      value === '' ? null : value
    )
  }

  return (
    <div data-testid="custom-form-modal">
      <ModalConfirmV2
        title={title}
        description={
          <div className={classes.headingContainer}>
            <Typography className={classes.heading}>Form fields</Typography>
            <Typography
              className={classes.modalDescription}
              data-testid="modal-description"
            >
              <span className={classes.headingSub}>
                First name, Last name, Email address and Company name
              </span>{' '}
              will be required/mandatory in the form. You can add custom fields
              as per your requirement.
            </Typography>
          </div>
        }
        content={
          <div
            className={classes.modalContentContainer}
            data-testid="modal-content-container"
          >
            <div className={classes.modalLinkContainer}>
              <div className={classes.customFieldContainer}>
                <FieldArray
                  name={`listingCardData.template.data.${
                    keyName === 'privateOffer'
                      ? 'privateOfferForm'
                      : 'requestDemoForm'
                  }.fieldsToDisplay`}
                >
                  {({ push, remove }) => (
                    <>
                      {customFormFields.map(
                        (customfield, index) =>
                          customFormFields && (
                            <div
                              className={classes.row}
                              data-testid="customFormField-row"
                            >
                              <div className={classes.titleAndIcon}>
                                <div className={classes.title}>
                                  Custom field {index + 1}
                                </div>
                                {!customfield?.isExisting && (
                                  <div className={clsx(classes.icon)}>
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      style={{ width: '18px', height: '20px' }}
                                      data-testid="trash-customFormField"
                                      onClick={() => {
                                        remove(index)
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              <div style={{ width: '100%' }}>
                                <Grid container spacing={3}>
                                  <Grid size={6}>
                                    <div
                                      key={`customFormFields-${randomKey}-displayName`}
                                      className={classes.customFieldTitle}
                                    >
                                      <TextInputV2
                                        keyName={`listingCardData.template.data.${
                                          keyName === 'privateOffer'
                                            ? 'privateOfferForm'
                                            : 'requestDemoForm'
                                        }.fieldsToDisplay.${index}.displayName`}
                                        title="Label"
                                        type="text"
                                        placeHolder="Label"
                                        maxCharacters={72}
                                        value={customfield?.displayName}
                                        isError={
                                          !!(
                                            get(
                                              customFormFieldsTouched,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.displayName`,
                                              false
                                            ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.displayName`,
                                              false
                                            )
                                          )
                                        }
                                        onChange={e => {
                                          const newDisplayName = e.target.value
                                          const newApiName =
                                            formatToApiName(newDisplayName)
                                          setFieldValue(
                                            `listingCardData.template.data.${
                                              keyName === 'privateOffer'
                                                ? 'privateOfferForm'
                                                : 'requestDemoForm'
                                            }.fieldsToDisplay.${index}.displayName`,
                                            newDisplayName
                                          )
                                          setFieldValue(
                                            `listingCardData.template.data.${
                                              keyName === 'privateOffer'
                                                ? 'privateOfferForm'
                                                : 'requestDemoForm'
                                            }.fieldsToDisplay.${index}.apiName`,
                                            newApiName
                                          )
                                        }}
                                        isRequired={false}
                                        isReadOnly={false}
                                        errorText={
                                          (!!get(
                                            customFormFieldsTouched,
                                            `fieldsToDisplay.${[
                                              index,
                                            ]}.displayName`,
                                            false
                                          ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.displayName`,
                                              ''
                                            )) ||
                                          ''
                                        }
                                        showSpinner={false}
                                        onBlur={handleBlur}
                                        updatedValue={customfield?.displayName}
                                      />
                                    </div>
                                  </Grid>
                                  <Grid size={6}>
                                    <div
                                      key={`customFormFields-${randomKey}-dataType`}
                                      className={classes.customFieldTitle}
                                    >
                                      <SelectV2
                                        keyName={`listingCardData.template.data.${
                                          keyName === 'privateOffer'
                                            ? 'privateOfferForm'
                                            : 'requestDemoForm'
                                        }.fieldsToDisplay.${index}.dataType`}
                                        field={{
                                          title: 'Select data type',
                                          placeHolder: 'Select data type',
                                          id: 'customFormFields.dataType',
                                          isRequired: false,
                                          humanReadableKey: 'selectDataType',
                                          isReadOnly: false,
                                          isCustom: false,
                                          defaultValue: customfield?.dataType,

                                          dataType: 'enum' as DataType,
                                          options: dataType,
                                        }}
                                        value={customfield?.dataType}
                                        onChange={e =>
                                          handleDataTypeChange(
                                            keyName,
                                            index,
                                            e.target.value as string
                                          )
                                        }
                                        onBlur={handleBlur}
                                        isError={
                                          !!(
                                            get(
                                              customFormFieldsTouched,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.dataType`,
                                              false
                                            ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.dataType`,
                                              false
                                            )
                                          )
                                        }
                                        errorText={
                                          (!!get(
                                            customFormFieldsTouched,
                                            `fieldsToDisplay.${[index]}.dataType`,
                                            false
                                          ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.dataType`,
                                              ''
                                            )) ||
                                          ''
                                        }
                                      />
                                    </div>
                                  </Grid>
                                </Grid>
                              </div>
                              <div style={{ width: '100%' }}>
                                <Grid container spacing={3}>
                                  <Grid size={6}>
                                    <div
                                      key={`customFormFields-${randomKey}-isRequired`}
                                      className={classes.customField}
                                    >
                                      <SelectV2
                                        keyName={`listingCardData.template.data.${
                                          keyName === 'privateOffer'
                                            ? 'privateOfferForm'
                                            : 'requestDemoForm'
                                        }.fieldsToDisplay.${index}.isRequired`}
                                        field={{
                                          title: 'Is required?',
                                          placeHolder: 'Is required?',
                                          id: 'customFormFields.isRequired',
                                          isRequired: false,
                                          humanReadableKey: 'isRequired',
                                          isReadOnly: false,
                                          isCustom: false,
                                          defaultValue: customfield.isRequired
                                            ? 'Yes'
                                            : 'No',
                                          dataType: 'enum' as DataType,
                                          options: requiredField,
                                        }}
                                        value={
                                          customfield.isRequired ? 'Yes' : 'No'
                                        }
                                        onChange={e =>
                                          handleIsRequiredChange(
                                            keyName,
                                            index,
                                            e.target.value as string
                                          )
                                        }
                                        onBlur={handleBlur}
                                        isError={
                                          !!(
                                            get(
                                              customFormFieldsTouched,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.isRequired`,
                                              false
                                            ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.isRequired`,
                                              false
                                            )
                                          )
                                        }
                                        errorText={
                                          (!!get(
                                            customFormFieldsTouched,
                                            `fieldsToDisplay.${[
                                              index,
                                            ]}.isRequired`,
                                            false
                                          ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.isRequired`,
                                              ''
                                            )) ||
                                          ''
                                        }
                                      />
                                    </div>
                                  </Grid>
                                  <Grid size={6}>
                                    <div
                                      key={`customFormFields-${randomKey}-validationType`}
                                      className={classes.customField}
                                    >
                                      <SelectV2
                                        keyName={`listingCardData.template.data.${
                                          keyName === 'privateOffer'
                                            ? 'privateOfferForm'
                                            : 'requestDemoForm'
                                        }.fieldsToDisplay.${index}.validationType`}
                                        field={{
                                          title: 'Validation type (Optional)',
                                          placeHolder:
                                            'Validation type (Optional)',
                                          id: 'customFormFields.validationType',
                                          isRequired: false,
                                          humanReadableKey: 'validationType',
                                          isReadOnly: false,
                                          isCustom: false,
                                          defaultValue: isEmpty(
                                            customfield.validationType
                                          )
                                            ? ''
                                            : customfield?.validationType,

                                          dataType: 'enum' as DataType,
                                          options: validationType,
                                        }}
                                        value={
                                          isEmpty(customfield.validationType)
                                            ? ''
                                            : customfield?.validationType
                                        }
                                        onChange={e =>
                                          handleValidationTypeChange(
                                            keyName,
                                            index,
                                            e.target.value as string
                                          )
                                        }
                                        onBlur={handleBlur}
                                        showNone
                                      />
                                    </div>
                                  </Grid>
                                </Grid>
                              </div>
                              <div style={{ width: '100%' }}>
                                <Grid container spacing={3}>
                                  <Grid size={6}>
                                    <div
                                      key={`customFormFields-${randomKey}-minLength`}
                                      className={classes.customField}
                                    >
                                      <TextInputV2
                                        keyName={`listingCardData.template.data.${
                                          keyName === 'privateOffer'
                                            ? 'privateOfferForm'
                                            : 'requestDemoForm'
                                        }.fieldsToDisplay.${index}.minLength`}
                                        title="Minimum character length for the value"
                                        type="number"
                                        placeHolder="Enter Minimum character length for the value"
                                        minNumber={
                                          customfield.isRequired ? 1 : 0
                                        }
                                        value={customfield?.minLength}
                                        isError={
                                          !!(
                                            get(
                                              customFormFieldsTouched,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.minLength`,
                                              false
                                            ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.minLength`,
                                              false
                                            )
                                          )
                                        }
                                        onChange={handleChange}
                                        isReadOnly={false}
                                        isRequired={false}
                                        errorText={
                                          (!!get(
                                            customFormFieldsTouched,
                                            `fieldsToDisplay.${[
                                              index,
                                            ]}.minLength`,
                                            false
                                          ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.minLength`,
                                              ''
                                            )) ||
                                          ''
                                        }
                                        showSpinner={false}
                                        onBlur={handleBlur}
                                        updatedValue={customfield?.minLength}
                                      />
                                    </div>
                                  </Grid>
                                  <Grid size={6}>
                                    <div
                                      key={`customFormFields-${randomKey}-maxLength`}
                                      className={classes.customField}
                                    >
                                      <TextInputV2
                                        keyName={`listingCardData.template.data.${
                                          keyName === 'privateOffer'
                                            ? 'privateOfferForm'
                                            : 'requestDemoForm'
                                        }.fieldsToDisplay.${index}.maxLength`}
                                        title="Maximum character length for the value"
                                        type="number"
                                        placeHolder="Enter a character length for the value"
                                        minNumber={
                                          Number(customfield?.minLength) + 1
                                        }
                                        value={customfield?.maxLength}
                                        isError={
                                          !!(
                                            get(
                                              customFormFieldsTouched,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.maxLength`,
                                              false
                                            ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.maxLength`,
                                              false
                                            )
                                          )
                                        }
                                        onChange={handleChange}
                                        isReadOnly={false}
                                        isRequired={false}
                                        errorText={
                                          (!!get(
                                            customFormFieldsTouched,
                                            `fieldsToDisplay.${[
                                              index,
                                            ]}.maxLength`,
                                            false
                                          ) &&
                                            get(
                                              customFormFieldsErrors,
                                              `fieldsToDisplay.${[
                                                index,
                                              ]}.maxLength`,
                                              ''
                                            )) ||
                                          ''
                                        }
                                        showSpinner={false}
                                        onBlur={handleBlur}
                                        updatedValue={customfield?.maxLength}
                                      />
                                    </div>
                                  </Grid>
                                </Grid>
                              </div>
                            </div>
                          )
                      )}
                      {customFormFields.length < 10 && (
                        <Typography
                          className={classes.linkText}
                          onClick={() =>
                            push({
                              apiName: '',
                              displayName: '',
                              isRequired: false,
                              dataType: 'string',
                              validationType: null,
                              minLength: 1,
                              maxLength: 99,
                            })
                          }
                          data-testid="add-customFormField-button"
                        >
                          Add a custom field
                        </Typography>
                      )}
                    </>
                  )}
                </FieldArray>
              </div>
            </div>
          </div>
        }
        open={open}
        rightHeaderContent={<img src={awsLogo} className={classes.awsImg} />}
        titleAcceptButton={'Save'}
        onDecline={handleClose}
        variant={ModalSizeVariants.large}
        hideAcceptButton={false}
        hideCancelButton={true}
        onAccept={handleClose}
        acceptDisabled={acceptButtonDisabled}
        icon={
          <FontAwesomeIcon
            icon={faShelves}
            style={{ width: '20px', height: '16px' }}
          />
        }
      />
    </div>
  )
}
